/**
 * Subscription status component.
 * Displays the current subscription status and provides upgrade options.
 */

import React, { useState } from 'react';
import { CheckCircle, XCircle, Loader2, CreditCard, X } from 'lucide-react';
import {
  useSubscriptionStatus,
  useSubscriptionLoading,
  useSubscriptionError,
} from '../stores/subscriptionStore';
import { subscriptionService } from '@/api/services/subscription-service';
import { PlanSelection } from './PlanSelection';
import { SUBSCRIPTION_PLANS } from '@/config/plan-config';

interface SubscriptionStatusProps {
  onUpgrade?: () => void;
  showUpgradeButton?: boolean;
  className?: string;
}

export const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  onUpgrade,
  showUpgradeButton = true,
  className = '',
}) => {
  const { isActive } = useSubscriptionStatus();
  const isLoading = useSubscriptionLoading();
  const error = useSubscriptionError();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlanId] = useState<string>(SUBSCRIPTION_PLANS[0].id);

  const handleUpgrade = () => {
    setShowPlanModal(true);
  };

  const handlePlanSelect = async (planId: string) => {
    try {
      const plan = SUBSCRIPTION_PLANS.find((p) => p.id === planId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      await subscriptionService.redirectToCheckout(plan.priceId);
      setShowPlanModal(false);
      onUpgrade?.();
    } catch (error) {
      console.error('Failed to start checkout:', error);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 text-gray-600 ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading subscription status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 text-red-600 ${className}`}>
        <XCircle className="h-4 w-4" />
        <span>Error loading subscription: {error}</span>
      </div>
    );
  }

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        {isActive ? (
          <>
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-green-600 font-medium">Active Subscription</span>
          </>
        ) : (
          <>
            <XCircle className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">No Active Subscription</span>
            {showUpgradeButton && (
              <button
                onClick={handleUpgrade}
                className="ml-2 inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
              >
                <CreditCard className="h-3 w-3" />
                Upgrade
              </button>
            )}
          </>
        )}
      </div>

      {/* Plan Selection Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Choose Your Plan</h2>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <PlanSelection
                selectedPlanId={selectedPlanId}
                onPlanSelect={handlePlanSelect}
                onContinue={() => setShowPlanModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionStatus;
